import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import Button from '@atlaskit/button/new';
import PageHeader from '@atlaskit/page-header';
import { SimpleTag } from '@atlaskit/tag';
import TagGroup from '@atlaskit/tag-group';
import Select from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import TextField from '@atlaskit/textfield';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  ApiRequestError,
  fetchActiveDemandPartners,
  supplyPartnerServices,
  type IActiveDPResponse,
  type SpConfigDetails
} from '@shared/api';
import { countryOptions } from '@shared/lib/constant';
import { useAppSelector } from '@shared/store/hooks';

import MultiSelectInput, { type MultiSelectOption } from '@shared/ui/MultiSelectInput';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type AdFormat = 'DISPLAY' | 'VIDEO';

type RuleDraft = {
  id: string;
  adFormat: AdFormat | '';
  countryCode: string;
  rateLimit: string;
};

type SelectOption = {
  label: string;
  value: string;
};

const DEFAULT_RATE_LIMIT = 100;

const cardStyles = {
  background: token('color.background.input'),
  border: `1px solid ${token('color.border')}`,
  borderRadius: '18px',
  padding: token('space.300')
} as const;

const dividerStyles = {
  background: token('color.border'),
  height: '1px',
  width: '100%'
} as const;

const appTagGroupStyles = {
  paddingBlockEnd: token('space.300'),
  paddingBlockStart: token('space.300'),
  paddingInlineEnd: 0,
  paddingInlineStart: 0
} as const;

const adFormatOptions: SelectOption[] = [
  { label: 'Display', value: 'DISPLAY' },
  { label: 'Video', value: 'VIDEO' }
];

const countrySelectOptions: SelectOption[] = countryOptions.map(({ code, label }) => ({
  label: `${label} (${code})`,
  value: code
}));

const createRuleDraft = (): RuleDraft => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  adFormat: '',
  countryCode: '',
  rateLimit: ''
});

const isMissingConfigError = (error: unknown) =>
  error instanceof ApiRequestError && error.status === 400;

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : 'Unable to load supply partner config.';

const buildAppsList = (configKeys: string[]) => {
  const appIds = [...new Set([...configKeys])];
  return Object.fromEntries(appIds.map((appId) => [appId, null])) as Record<string, null>;
};

const parseRuleDrafts = (rateLimitConfig: Record<string, number>): RuleDraft[] =>
  Object.entries(rateLimitConfig)
    .filter(([key]) => key !== 'DEFAULT')
    .map(([key, value], index) => {
      const [adFormat = '', countryCode = ''] = key.split('-');
      return {
        id: `${key}-${index}`,
        adFormat: adFormat === 'DISPLAY' || adFormat === 'VIDEO' ? adFormat : '',
        countryCode,
        rateLimit: String(value)
      };
    });

const buildConfigDraft = (
  config: SpConfigDetails | undefined,
): SpConfigDetails => ({
  appsList: buildAppsList(Object.keys(config?.appsList ?? {})),
  blockedDemandPartnerList: config?.blockedDemandPartnerList ?? {},
  rateLimitConfig: {
    DEFAULT: DEFAULT_RATE_LIMIT,
    ...Object.fromEntries(
      Object.entries(config?.rateLimitConfig ?? {}).filter(([key]) => key !== 'DEFAULT')
    )
  }
});

export default function SupplyPartnerConfigPage() {
  const { spId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const supplyPartnerProfile = useAppSelector((state) =>
    spId ? state.partner.supplyPartnerProfiles[spId] : undefined
  );
  const inventoryDemandPartners = useAppSelector((state) => state.inventory.activeDemandPartners);

  const [draft, setDraft] = useState<SpConfigDetails | undefined>(undefined);
  const [ruleDrafts, setRuleDrafts] = useState<RuleDraft[]>([]);

  const breadcrumbs = (
    <Breadcrumbs>
      <BreadcrumbsItem text="Dashboard" key="Dashboard" onClick={() => navigate('/home')} />
      <BreadcrumbsItem
        text="Supply Partners"
        key="Supply Partners"
        onClick={() => navigate('/supply-partners')}
      />
      <BreadcrumbsItem
        text={spId}
        key={spId}
        onClick={() => navigate(`/partner/sp/${spId}`)}
      />
      <BreadcrumbsItem text="Config" key="Config" />
    </Breadcrumbs>
  );


  const configQuery = useQuery({
    queryKey: ['supplyPartnerConfigDetails', spId],
    queryFn: (): Promise<SpConfigDetails> => supplyPartnerServices.getSpConfigDetails(spId),
    enabled: Boolean(spId),
    retry: false
  });

  const demandPartnerOptionsQuery = useQuery({
    queryKey: ['activeDemandPartnersForConfig'],
    queryFn: (): Promise<IActiveDPResponse[]> => fetchActiveDemandPartners(),
    enabled: inventoryDemandPartners.length === 0
  });

  const demandPartners = demandPartnerOptionsQuery.data ?? inventoryDemandPartners;

  useEffect(() => {
    if ( configQuery.isLoading) {
      return;
    }

    if (configQuery.error && !isMissingConfigError(configQuery.error)) {
      return;
    }

    const nextDraft = buildConfigDraft(configQuery.data);
    setDraft(nextDraft);
    setRuleDrafts(parseRuleDrafts(nextDraft.rateLimitConfig));
  }, [
    configQuery.data,
    configQuery.error,
    configQuery.isLoading
  ]);

  const blockedDemandPartnerOptions = useMemo<MultiSelectOption[]>(
    () =>
      demandPartners.map((partner) => ({
        label: `${partner.dpId} • ${partner.dpAlias || partner.dpIdentifier || 'Demand partner'}`,
        value: partner.dpId
      })),
    [demandPartners]
  );

  const selectedBlockedDemandPartners = useMemo(
    () =>
      blockedDemandPartnerOptions.filter(
        (option) => Boolean(Object.hasOwn(draft?.blockedDemandPartnerList || {}, option.value))
      ),
    [blockedDemandPartnerOptions, draft?.blockedDemandPartnerList]
  );

  const appsListEntries = useMemo(
    () =>
      Object.keys(draft?.appsList ?? []),
    [draft?.appsList]
  );

  const duplicateRuleKeys = useMemo(() => {
    const nextCounts = new Map<string, number>();

    ruleDrafts.forEach((rule) => {
      if (!rule.adFormat || !rule.countryCode) {
        return;
      }

      const key = `${rule.adFormat}-${rule.countryCode}`;
      nextCounts.set(key, (nextCounts.get(key) ?? 0) + 1);
    });

    return new Set(
      [...nextCounts.entries()].filter(([, count]) => count > 1).map(([key]) => key)
    );
  }, [ruleDrafts]);

  const rulesValidationMessage = useMemo(() => {
    if (
      ruleDrafts.some(
        (rule) =>
          !rule.adFormat ||
          !rule.countryCode ||
          !rule.rateLimit.trim() ||
          Number.isNaN(Number(rule.rateLimit)) ||
          Number(rule.rateLimit) < 0
      )
    ) {
      return 'Complete each added rule with ad format, country, and a valid rate limit.';
    }

    if (duplicateRuleKeys.size > 0) {
      return 'Each ad format and country combination must be unique.';
    }

    return '';
  }, [duplicateRuleKeys, ruleDrafts]);

  const updateConfigMutation = useMutation({
    mutationFn: (payload: SpConfigDetails) => supplyPartnerServices.updateSpConfigDetails(spId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['supplyPartnerConfigDetails', spId] });
    }
  });

  const isLoading =
    configQuery.isLoading || demandPartnerOptionsQuery.isLoading;

  const savePayload = useMemo<SpConfigDetails | undefined>(() => {
    if (!draft || rulesValidationMessage) {
      return undefined;
    }

    const nextRateLimitConfig = ruleDrafts.reduce<Record<string, number>>(
      (accumulator, rule) => {
        if (!rule.adFormat || !rule.countryCode) {
          return accumulator;
        }

        accumulator[`${rule.adFormat}-${rule.countryCode}`] = Number(rule.rateLimit);
        return accumulator;
      },
      { DEFAULT: DEFAULT_RATE_LIMIT }
    );

    return {
      appsList: draft.appsList,
      blockedDemandPartnerList: draft.blockedDemandPartnerList,
      rateLimitConfig: nextRateLimitConfig
    };
  }, [draft, ruleDrafts, rulesValidationMessage]);

  if (!spId) {
    return (
      <Stack space="space.300" xcss={{ width: '100%' }}>
        <PageHeader breadcrumbs={breadcrumbs}>Supply Partner Config</PageHeader>
        <Text>Supply partner ID not found.</Text>
      </Stack>
    );
  }

  if (isLoading || !draft) {
    return (
      <Box
        style={{
          alignItems: 'center',
          display: 'flex',
          height: '70vh',
          justifyContent: 'center'
        }}
      >
        <Spinner size="large" />
      </Box>
    );
  }


  if (configQuery.error && !isMissingConfigError(configQuery.error)) {
    return (
      <Stack space="space.300" xcss={{ width: '100%' }}>
        <PageHeader breadcrumbs={breadcrumbs}>Supply Partner Config</PageHeader>
        <Text color="color.text.danger">{getErrorMessage(configQuery.error)}</Text>
      </Stack>
    );
  }

  return (
    <Stack space="space.300" xcss={{ width: '100%' }}>
      <PageHeader
        breadcrumbs={breadcrumbs}
        actions={
          <Inline space="space.100">
            <Button appearance="subtle" onClick={() => navigate(`/partner/sp/${spId}`)}>
              Back to details
            </Button>
            <Button
              appearance="primary"
              isDisabled={!savePayload}
              isLoading={updateConfigMutation.isPending}
              onClick={() => {
                if (savePayload) {
                  updateConfigMutation.mutate(savePayload);
                }
              }}
            >
              Save config
            </Button>
          </Inline>
        }
      >
        {supplyPartnerProfile?.spName ?? `Supply Partner ${spId}`} Config
      </PageHeader>

      <Box
        style={{
          alignItems: 'center',
          background: token('color.background.neutral.subtle'),
          borderRadius: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          padding: token('space.250')
        }}
      >
        <div>
          <Text size="large" weight="bold">
            {spId}
          </Text>
          <Text color="color.text.subtle">
            Manage blocked demand partners and rate-limit rules for this supply partner.
          </Text>
        </div>
      </Box>

      {updateConfigMutation.isSuccess ? (
        <Text color="color.text.success">Config updated successfully.</Text>
      ) : null}
      {updateConfigMutation.error ? (
        <Text color="color.text.danger">{getErrorMessage(updateConfigMutation.error)}</Text>
      ) : null}

      <Box style={cardStyles}>
        <Stack space="space.200">
          <Text size="large" weight="medium">
            App List
          </Text>
          <Text color="color.text.subtle">
            Apps are shown for reference, but they are not editable here.
          </Text>
          <Box style={dividerStyles} />
          {appsListEntries.length > 0 ? (
            <div style={appTagGroupStyles}>
              <TagGroup label="Apps">
                {appsListEntries.map((app) => (
                  <SimpleTag key={app} text={app} />
                ))}
              </TagGroup>
            </div>
          ) : (
            <Text color="color.text.subtle">No active apps available for this supply partner.</Text>
          )}
        </Stack>
      </Box>

      <Box style={cardStyles}>
        <Stack space="space.200">
          <Text size="large" weight="medium">
            Blocked Demand Partners
          </Text>
          <MultiSelectInput
            inputId="blocked-demand-partners"
            label="Blocked demand partner list"
            options={blockedDemandPartnerOptions}
            value={selectedBlockedDemandPartners}
            onChange={(value) => {
              setDraft((currentDraft) => {
                if (!currentDraft) {
                  return currentDraft;
                }

                return {
                  ...currentDraft,
                  blockedDemandPartnerList: Object.fromEntries(
                    value.map((option) => [option.value, null])
                  )
                };
              });
            }}
            placeholder="Select demand partners to block"
            // helpText="Selected demand partners will be sent as keys in blockedDemandPartnerList."
            enableSelectAll
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          />
        </Stack>
      </Box>

      <Box style={cardStyles}>
        <Stack space="space.250">
          <Inline spread="space-between" alignBlock="center">
            <div>
              <Text size="large" weight="medium">
                Rate Limit Config
              </Text>
              <Text color="color.text.subtle">
                Default rate limit is always sent as `DEFAULT: 100`.
              </Text>
            </div>
            <Button
              appearance="primary"
              onClick={() => {
                setRuleDrafts((currentRules) => [...currentRules, createRuleDraft()]);
              }}
            >
              Add Rules
            </Button>
          </Inline>

          <Box style={dividerStyles} />

          <div
            style={{
              display: 'grid',
              gap: token('space.250'),
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))'
            }}
          >
            <div>
              <Stack space="space.050">
                <Text color="color.text.subtle" size="small">
                  Default key
                </Text>
                <TextField isDisabled value="DEFAULT" />
              </Stack>
            </div>
            <div>
              <Stack space="space.050">
                <Text color="color.text.subtle" size="small">
                  Default rate limit
                </Text>
                <TextField isDisabled type="number" value={String(DEFAULT_RATE_LIMIT)} />
              </Stack>
            </div>
          </div>

          {ruleDrafts.length > 0 ? (
            <Stack space="space.200">
              {ruleDrafts.map((rule, index) => {
                const duplicateKey =
                  rule.adFormat && rule.countryCode
                    ? `${rule.adFormat}-${rule.countryCode}`
                    : '';
                const isDuplicate = duplicateKey ? duplicateRuleKeys.has(duplicateKey) : false;

                return (
                  <Box
                    key={rule.id}
                    style={{
                      background: token('color.background.neutral.subtle'),
                      borderRadius: '16px',
                      padding: token('space.200')
                    }}
                  >
                    <Stack space="space.150">
                      <Inline spread="space-between" alignBlock="center">
                        <Text weight="medium">Rule {index + 1}</Text>
                        <Button
                          appearance="subtle"
                          onClick={() => {
                            setRuleDrafts((currentRules) =>
                              currentRules.filter((currentRule) => currentRule.id !== rule.id)
                            );
                          }}
                        >
                          Remove
                        </Button>
                      </Inline>

                      <div
                        style={{
                          display: 'grid',
                          gap: token('space.200'),
                          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))'
                        }}
                      >
                        <div>
                          <Stack space="space.050">
                            <Text color="color.text.subtle" size="small">
                              Ad format
                            </Text>
                            <Select<SelectOption>
                              options={adFormatOptions}
                              placeholder="Select format"
                              value={
                                adFormatOptions.find((option) => option.value === rule.adFormat) ??
                                null
                              }
                              onChange={(option) => {
                                setRuleDrafts((currentRules) =>
                                  currentRules.map((currentRule) =>
                                    currentRule.id === rule.id
                                      ? {
                                          ...currentRule,
                                          adFormat: ((option as SelectOption | null)?.value ??
                                            '') as AdFormat | '',
                                          rateLimit:
                                            currentRule.rateLimit ||
                                            String(DEFAULT_RATE_LIMIT)
                                        }
                                      : currentRule
                                  )
                                );
                              }}
                              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                              menuPosition="fixed"
                            />
                          </Stack>
                        </div>

                        <div>
                          <Stack space="space.050">
                            <Text color="color.text.subtle" size="small">
                              Country
                            </Text>
                            <Select<SelectOption>
                              options={countrySelectOptions}
                              placeholder="Select country"
                              value={
                                countrySelectOptions.find(
                                  (option) => option.value === rule.countryCode
                                ) ?? null
                              }
                              onChange={(option) => {
                                setRuleDrafts((currentRules) =>
                                  currentRules.map((currentRule) =>
                                    currentRule.id === rule.id
                                      ? {
                                          ...currentRule,
                                          countryCode: (option as SelectOption | null)?.value ?? '',
                                          rateLimit:
                                            currentRule.rateLimit ||
                                            String(DEFAULT_RATE_LIMIT)
                                        }
                                      : currentRule
                                  )
                                );
                              }}
                              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                              menuPosition="fixed"
                            />
                          </Stack>
                        </div>

                        <div>
                          <Stack space="space.050">
                            <Text color="color.text.subtle" size="small">
                              Rate limit
                            </Text>
                            <TextField
                              min={0}
                              type="number"
                              value={rule.rateLimit}
                              onChange={(event) => {
                                const nextRateLimit = event.currentTarget.value;
                                setRuleDrafts((currentRules) =>
                                  currentRules.map((currentRule) =>
                                    currentRule.id === rule.id
                                      ? { ...currentRule, rateLimit: nextRateLimit }
                                      : currentRule
                                  )
                                );
                              }}
                            />
                          </Stack>
                        </div>
                      </div>

                      {isDuplicate ? (
                        <Text color="color.text.danger" size="small">
                          This ad format and country combination is already used in another rule.
                        </Text>
                      ) : null}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Text color="color.text.subtle">
              No custom rules yet. Click Add Rules to configure a country and ad format specific limit.
            </Text>
          )}

          {rulesValidationMessage ? (
            <Text color="color.text.danger">{rulesValidationMessage}</Text>
          ) : null}
        </Stack>
      </Box>
    </Stack>
  );
}
